import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../api/request';
import apiRoutes from '../api/apiRoutes';
import { BookOpen, ArrowLeft, FileText } from 'lucide-react';
import CitationContent from '../components/CitationContent';

// Recursive component to render the tree index with numbering
const TreeNode = ({ node, onSelect, selectedNodeId, numberPrefix = '' }) => {
  // Calculate the current node's number
  const currentNumber = node && numberPrefix ? `${numberPrefix}` : '';
  // Determine if this is a parent node (no dots in numberPrefix, e.g., "1" vs "1.1")
  const isParent = currentNumber && !currentNumber.includes('.');

  const handleHover = () => {
    console.log(`Hovering over node: ${node?.title || 'unknown'}`); // Debug log
  };

  return (
    <div className="ml-1 mb-2">
      <div className={`relative cursor-pointer py-1 pl-4 pr-2 text-sm ${isParent ? 'font-semibold' : 'font-normal'} text-gray-800 group transition-colors ${
        node?.id === selectedNodeId ? 'text-black font-medium' : ''
      }`}
        onClick={() => node && onSelect(node.id)}
        onMouseEnter={handleHover}
      >
        {node?.id === selectedNodeId && (
          <span className="absolute left-0 p-1 h-1/2 w-0.5 bg-yellow-300 rounded-sm justify-center mt-0.5" />
        )}
        <span className="underline-animation">
          {currentNumber && <span className="mr-2 text-gray-500">{currentNumber}</span>}
          {node?.title || 'Untitled'}
        </span>
      </div>
      {node?.children && node.children.length > 0 && (
        <div className="ml-2">
          {node.children.map((child, index) => (
            <TreeNode
              key={child?.id || `child-${index}`}
              node={child}
              onSelect={onSelect}
              selectedNodeId={selectedNodeId}
              numberPrefix={`${currentNumber ? `${currentNumber}.` : ''}${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Component to render a single A4 page with content
const A4Page = ({ node, selectedNodeId, contentRefs }) => {
  return (
    <div
      ref={el => node && (contentRefs.current[node.id] = el)}
      className="bg-white p-8 w-[210mm] min-h-[297mm] shadow-lg rounded-lg border border-gray-200 break-after-page"
      style={{
        pageBreakAfter: 'always',
        pageBreakInside: 'avoid',
        boxSizing: 'border-box',
      }}
    >
      <h1 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-2">
        {node?.header || node?.title || 'Untitled'}
      </h1>
      <CitationContent node={node} selectedNodeId={selectedNodeId} />
    </div>
  );
};

const ViewBook = () => {
  const location = useLocation();
  const { id: routeId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const queryId = queryParams.get('id');
  const bookId = routeId || queryId;
  const navigate = useNavigate();
  
  const [bookData, setBookData] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRefs = useRef({});

  useEffect(() => {
    const fetchBookData = async () => {
      if (!bookId) {
        setError('No book ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await apiGet(apiRoutes.getBookContents(bookId));
        const normalizedData = Array.isArray(data) ? data : data && typeof data === 'object' ? [data] : [];
        setBookData(normalizedData);
        if (normalizedData.length > 0 && normalizedData[0]?.id) {
          setSelectedNodeId(normalizedData[0].id);
        }
      } catch (err) {
        setError('Failed to fetch book contents');
      } finally {
        setLoading(false);
      }
    };
    fetchBookData();
  }, [bookId]);

  useEffect(() => {
    if (selectedNodeId && contentRefs.current[selectedNodeId]) {
      contentRefs.current[selectedNodeId].scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedNodeId]);

  // Flatten nodes while preserving hierarchical order
  const flattenNodes = (nodes) => {
    let result = [];
    const nodeArray = Array.isArray(nodes) ? nodes : nodes && typeof nodes === 'object' ? [nodes] : [];
    
    nodeArray.forEach(node => {
      if (node && typeof node === 'object') {
        // Add the current node
        result.push(node);
        // Recursively add children if they exist
        if (node.children && Array.isArray(node.children)) {
          result = result.concat(flattenNodes(node.children));
        }
      }
    });
    return result;
  };

  const flatNodes = flattenNodes(bookData);

  // Split content into A4 pages if it overflows
  const splitIntoPages = (nodes) => {
    const pages = [];
    nodes.forEach(node => {
      // Each node gets at least one page
      if (node) pages.push({ ...node });
    });
    return pages;
  };

  const pages = splitIntoPages(flatNodes);

  // Determine the book title
  const bookTitle = bookData?.[0]?.title || 'Book Title';

  if (!bookId) return <div className="text-center p-4 text-red-500 font-medium">No book ID provided</div>;
  if (loading) return <div className="text-center p-4 text-gray-600">Loading...</div>;
  if (error) return <div className="text-center p-4 text-red-500 font-medium">{error}</div>;
  if (flatNodes.length === 0) return <div className="text-center p-4 text-gray-600">No book content available</div>;

  return (
    <>
      <style>
        {`
          .underline-animation {
            position: relative;
            display: inline-block;
          }
          .underline-animation::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 0;
            height: 2px;
            background-color: #000000;
            transition: width 0.6s linear;
            z-index: 1;
          }
          .group:hover .underline-animation::after {
            width: 100%;
          }
        `}
      </style>
      <div className="flex h-screen bg-gray-100">
        {/* Left Sidebar: Book Index */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto p-6">
          <div className="flex items-center space-x-2 mb-2">
            <button
              onClick={() => navigate('/books')}
              className="p-1 hover:bg-gray-50 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-md mt-2 font-semibold text-gray-800">{bookTitle}</h1>
          </div>
          <hr className="border-gray-200 my-1" />
          <div className="flex items-center mt-2 space-x-2 mb-2">
            <FileText className="w-5 h-5  text-gray-600" />
            <h2 className="text-sm mt-2 text-gray-800">Table of Contents</h2>
          </div>
          <hr className="border-gray-200 my-1 mb-5" />
          {bookData.map((node, index) => (
            <TreeNode
              key={node?.id || `node-${index}`}
              node={node}
              onSelect={setSelectedNodeId}
              selectedNodeId={selectedNodeId}
              numberPrefix={`${index + 1}`}
            />
          ))}
        </div>

        {/* Right Content: Book Pages */}
        <div className="flex-1 p-12 overflow-y-auto bg-gray-50">
          <div className="max-w-[210mm] mx-auto space-y-8">
            {pages.map((node, index) => (
              <A4Page
                key={node?.id || `page-${index}`}
                node={node}
                selectedNodeId={selectedNodeId}
                contentRefs={contentRefs}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewBook;